import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { ModalDirective } from 'ngx-bootstrap';
import { Subscription } from 'rxjs';
import { ConfigurationService } from '../../services/configuration.service';
import { AccountService } from '../../services/account.service';
import { AlertService, MessageSeverity } from '../../services/alert.service';
import { Permission } from '../../models/permission.model';

// Merged HCPs / HCOs (route merge-history/hcp or merge-history/hco): the merges in effect, the merges that were undone,
// and undoing one. An unmerge shows a preview of what will be restored and asks the user to type the record's ID first.
@Component({
  selector: 'crm-merge-history',
  templateUrl: './merge-history.component.html'
})
export class MergeHistoryComponent implements OnInit, OnDestroy {
  kind: 'hcp' | 'hco' = 'hcp';
  label = 'HCP';
  pageSize = 20;

  search = '';
  fromDate: string;
  toDate: string;
  merged: any[] = [];
  mergedTotal = 0;
  mergedPage = 1;
  loadingMerged = false;

  undone: any[] = [];
  undoneTotal = 0;
  undonePage = 1;
  loadingUndone = false;

  @ViewChild('unmergeModal')
  unmergeModal: ModalDirective;
  selected: any;
  plan: any;
  loadingPlan = false;
  confirmText = '';
  unmerging = false;

  readonly entityNames: { [entity: string]: string } = {
    HCPHCO: 'HCP - HCO links', HCPBrandHCPClass: 'Brand classes', RequestHcp: 'Requests', Visit: 'Visits', UserHcp: 'Rep links',
    HCOBusinessUnitHCPClass: 'Classes', RepActivityAttendee: 'Activity attendees', RequestHco: 'Requests', PlanVisit: 'Plan visits',
    UserPharmacyHcos: 'Pharmacy links', UserHco: 'Rep links'
  };

  private routeSub: Subscription;

  constructor(private http: HttpClient, private configurations: ConfigurationService, private route: ActivatedRoute,
    private accountService: AccountService, private alertService: AlertService) { }

  ngOnInit() {
    this.routeSub = this.route.paramMap.subscribe(params => {
      this.kind = params.get('kind') === 'hco' ? 'hco' : 'hcp';
      this.label = this.kind.toUpperCase();
      this.mergedPage = 1;
      this.undonePage = 1;
      this.loadMerged();
      this.loadUndone();
    });
  }

  ngOnDestroy() {
    if (this.routeSub) this.routeSub.unsubscribe();
  }

  get canUnmerge(): boolean {
    return this.accountService.userHasPermission(this.kind === 'hcp' ? Permission.unmergeHcpsPermission : Permission.unmergeHcosPermission);
  }

  private get api() {
    return this.configurations.apiAddress + '/api/mergeHistory/' + this.kind;
  }

  pages(total: number): number {
    return Math.max(1, Math.ceil(total / this.pageSize));
  }

  loadMerged(page: number = this.mergedPage) {
    this.mergedPage = page;
    this.loadingMerged = true;
    let params = new HttpParams().set('pageNumber', String(page)).set('pageSize', String(this.pageSize));
    if (this.search) params = params.set('search', this.search);
    if (this.fromDate) params = params.set('fromDate', this.fromDate);
    if (this.toDate) params = params.set('toDate', this.toDate);
    this.http.get<any>(this.api + '/merged', { params }).subscribe(r => {
      this.merged = r.items;
      this.mergedTotal = r.totalCount;
      this.loadingMerged = false;
    }, error => {
      this.loadingMerged = false;
      this.alertService.showStickyMessage('Could not load the merged ' + this.label + 's', null, MessageSeverity.Error, error);
    });
  }

  loadUndone(page: number = this.undonePage) {
    this.undonePage = page;
    this.loadingUndone = true;
    const params = new HttpParams().set('pageNumber', String(page)).set('pageSize', String(this.pageSize));
    this.http.get<any>(this.api + '/undone', { params }).subscribe(r => {
      this.undone = r.items;
      this.undoneTotal = r.totalCount;
      this.loadingUndone = false;
    }, error => {
      this.loadingUndone = false;
      this.alertService.showStickyMessage('Could not load the undone merges', null, MessageSeverity.Error, error);
    });
  }

  openUnmerge(row: any) {
    this.selected = row;
    this.plan = null;
    this.confirmText = '';
    this.loadingPlan = true;
    this.unmergeModal.show();
    this.http.get<any>(this.api + '/' + row.absorbedId + '/unmergePreview').subscribe(p => {
      this.plan = p;
      this.loadingPlan = false;
    }, error => {
      this.loadingPlan = false;
      this.unmergeModal.hide();
      this.alertService.showStickyMessage('Could not prepare the unmerge', null, MessageSeverity.Error, error);
    });
  }

  get confirmed(): boolean {
    return this.plan && this.plan.canUnmerge && this.confirmText.trim() === String(this.plan.absorbedId);
  }

  entityName(entity: string): string {
    return this.entityNames[entity] || entity;
  }

  unmerge() {
    if (!this.confirmed) return;
    this.unmerging = true;
    this.http.post<any>(this.api + '/' + this.plan.absorbedId + '/unmerge', null).subscribe(result => {
      this.unmerging = false;
      this.unmergeModal.hide();
      this.alertService.showMessage('Unmerged', this.label + ' #' + result.absorbedId + ' is separated from #' + result.survivingId + ' again.', MessageSeverity.Success);
      this.loadMerged();
      this.loadUndone(1);
    }, error => {
      this.unmerging = false;
      const problem = error && error.error && error.error.problem;
      this.alertService.showStickyMessage('Unmerge failed', problem || 'Nothing was changed.', MessageSeverity.Error, problem ? null : error);
    });
  }
}
